import { Mutation } from "@apollo/client/react/components";
import React from "react";
import { useSelfie } from "~/components/auth/Auth";
import useIsMobile from "~/lib/hooks/useIsMobile";
import { Modal, Box, Button } from "@govlaunch/core";
import ProfilePicture from "@govlaunch/profile-picture";
import { Margin } from "~/components/spacings";
import apostrophe from "~/lib/utils/apostrophe";
import { IProduct } from "~/types/types";
import * as palette from "@govlaunch/palette";
import {
  ISetGroupProductsMutationVariables,
  ISetGroupProductsMutation,
} from "~/lib/mutations/__generated__/SetGroupProducts.generated";
import SetGroupProducts from "~/lib/mutations/SetGroupProducts";
import { Form, Field } from "react-final-form";
import { SearchAlgoliaIndex } from "@govlaunch/algolia";
import { StoryProductSelect } from "@govlaunch/select";
import { FORM_ERROR } from "final-form";
import isObjectId from "~/lib/utils/isObjectId";
import { IGroupProps } from "~/components/group/IGroupProps";
import { FormattedMessage, useIntl } from "react-intl";

interface IGroupProductsModalProps extends IGroupProps {
  toggleOpen: () => void;
  isOpen: boolean;
}

export default function GroupProductsModal({ group, toggleOpen, isOpen }: IGroupProductsModalProps) {
  const intl = useIntl();
  const user = useSelfie();
  const isMobile = useIsMobile();

  const maxProductSuggestions = intl.formatMessage({
    defaultMessage: `You've reached the limit of 50 product suggestions per day`,
    id: "Hbm59/",
    description: "Error message for when a user reaches the limit of product suggestions",
  });

  const couldntSave = intl.formatMessage({
    defaultMessage: `Couldn't save, try again later.`,
    id: "wiA+Qd",
  });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={toggleOpen}
      ariaHideApp={false}
      overlayStyle={{
        zIndex: 1000,
      }}
      style={{
        overflow: "visible",
      }}
    >
      <Box p={15} width={[340, 560, 560, 768]}>
        <div
          css={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <ProfilePicture size={isMobile ? 32 : 42} image={group.logo} name={group.name} />

            <Margin ml={10}>
              <h3
                css={{
                  fontSize: isMobile ? 16 : 23,
                  margin: 0,
                  color: palette.darkGray,
                }}
              >
                <FormattedMessage
                  defaultMessage="Edit {groupName} products"
                  id="fKF/jQ"
                  values={{ groupName: apostrophe(group.name) }}
                />
              </h3>
            </Margin>
          </div>

          <span
            onClick={toggleOpen}
            role="button"
            aria-label="Close"
            css={{
              marginLeft: "auto",
              alignSelf: isMobile ? "auto" : "flex-start",
              color: palette.sealBlue,
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              "&:hover": {
                color: palette.mediumGray,
              },
            }}
          >
            <span
              css={{
                fontSize: 22,
              }}
            >
              &times;
            </span>
            <Margin
              ml={2.5}
              css={{
                display: "inline-flex",
              }}
            >
              {!isMobile && (
                <span
                  css={{
                    fontSize: 12,
                    textTransform: "uppercase",
                  }}
                >
                  <FormattedMessage defaultMessage="Close" id="rbrahO" />
                </span>
              )}
            </Margin>
          </span>
        </div>

        <Margin mt={20}>
          <Mutation<ISetGroupProductsMutation, ISetGroupProductsMutationVariables> mutation={SetGroupProducts}>
            {(setGroupProducts) => {
              return (
                <Form
                  onSubmit={async ({ products }: any) => {
                    const outsideProducts = products.filter((product: any) => product.id);
                    const govlaunchProducts = products.filter((product: any) => product._id);

                    try {
                      await setGroupProducts({
                        variables: {
                          id: group._id,
                          products: govlaunchProducts.map((product: IProduct) => product._id),
                          outsideProducts: outsideProducts.map((outsideProduct: any) => {
                            return {
                              _id: isObjectId(outsideProduct.id) ? outsideProduct.id : null,
                              name: outsideProduct.name,
                              url: outsideProduct.url,
                            };
                          }),
                        },
                      });

                      toggleOpen();

                      return null;
                    } catch (e: any) {
                      if (/maximum number of product suggestions/i.test(e.message)) {
                        return {
                          [FORM_ERROR]: maxProductSuggestions,
                        };
                      }

                      return {
                        [FORM_ERROR]: couldntSave,
                      };
                    }
                  }}
                  initialValues={{
                    products: [
                      ...group.products,
                      ...group.outsideProducts!.map((outsideProduct) => ({
                        id: outsideProduct._id,
                        name: outsideProduct.name,
                        logo: outsideProduct.logo,
                        url: outsideProduct.url,
                      })),
                    ],
                  }}
                >
                  {({ handleSubmit, submitError }) => (
                    <form onSubmit={handleSubmit}>
                      <Field name="products">
                        {({ input }) => (
                          <SearchAlgoliaIndex
                            index="products"
                            params={{
                              restrictSearchableAttributes: ["name"],
                            }}
                            user={user}
                          >
                            {({ results: products, searchValue: inputValue, onChange: onInputValueChange }: any) => (
                              <StoryProductSelect
                                products={input.value}
                                placeholder={intl.formatMessage({
                                  defaultMessage: "What are the products you want to add?",
                                  id: "cbABH5",
                                })}
                                inputValue={inputValue}
                                onInputValueChange={onInputValueChange}
                                selectableProducts={products}
                                onChange={input.onChange}
                                css={{
                                  minWidth: 300,
                                }}
                              />
                            )}
                          </SearchAlgoliaIndex>
                        )}
                      </Field>

                      <Margin
                        mt={20}
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Button type="submit" flex={0.7} theme="primary" color="success">
                          <FormattedMessage defaultMessage="Save Changes" id="3VI9mt" />
                        </Button>

                        <Margin
                          ml={10}
                          style={{
                            flex: 0.3,
                          }}
                        >
                          <Button type="button" width="100%" theme="secondary" color="success" onClick={toggleOpen}>
                            <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
                          </Button>
                        </Margin>
                      </Margin>

                      {submitError && (
                        <Margin mt={8}>
                          <div css={{ color: palette.red, fontSize: 14 }}>{submitError}</div>
                        </Margin>
                      )}
                    </form>
                  )}
                </Form>
              );
            }}
          </Mutation>
        </Margin>
      </Box>
    </Modal>
  );
}
